// components/NBAScoreBoard.jsx
import React, { useState, useEffect } from "react";

const NBAScoreBoard = ({
  teamAbbr = "LAL",        // Change this to the abbreviation of your team of choice
  refreshInterval = 5000,   // Refresh every 5 seconds
}) => {
  // Explicit NBA API URL
  const apiUrl = "https://site.api.espn.com/apis/site/v2/sports/basketball/nba/scoreboard";

  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchScoreData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      // Look for an event that includes our team.
      // Depending on the returned data structure, the team identifier may be in `team.abbreviation`
      const game = data.events.find((event) => {
        const competitors = event.competitions?.[0]?.competitors;
        return competitors?.some((comp) =>
          comp.team.abbreviation &&
          comp.team.abbreviation.toUpperCase() === teamAbbr.toUpperCase()
        );
      });

      setScoreData(game);
      setError(null);
    } catch (err) {
      setError(err);
      setScoreData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchScoreData();
    const intervalId = setInterval(fetchScoreData, refreshInterval);
    return () => clearInterval(intervalId);
  }, [apiUrl, teamAbbr, refreshInterval]);

  if (loading) return <div>Loading NBA scores...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!scoreData) return <div>No game found for team "{teamAbbr}".</div>;

  // Assuming at least one competition exists.
  const competition = scoreData.competitions[0];
  const competitors = competition.competitors;

  // Identify our team and the opponent.
  const teamData = competitors.find(
    (comp) =>
      comp.team.abbreviation &&
      comp.team.abbreviation.toUpperCase() === teamAbbr.toUpperCase()
  );
  const opponentData = competitors.find(
    (comp) =>
      comp.team.abbreviation &&
      comp.team.abbreviation.toUpperCase() !== teamAbbr.toUpperCase()
  );

  // Use ESPN's provided game status
  const gameStatus =
    scoreData.status?.type?.shortDetail ||
    scoreData.status?.type?.description ||
    "Live";

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>
        {teamData.team.displayName} vs {opponentData.team.displayName}
      </h2>
      <p style={styles.status}>Status: {gameStatus}</p>
      <div style={styles.scoreRow}>
        <div style={styles.team}>
          <img
            src={teamData.team.logo}
            alt={`${teamData.team.displayName} logo`}
            style={styles.logo}
          />
          <p style={styles.teamName}>{teamData.team.displayName}</p>
          <p style={styles.score}>Score: {teamData.score}</p>
        </div>
        <div style={styles.team}>
          <img
            src={opponentData.team.logo}
            alt={`${opponentData.team.displayName} logo`}
            style={styles.logo}
          />
          <p style={styles.teamName}>{opponentData.team.displayName}</p>
          <p style={styles.score}>Score: {opponentData.score}</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    border: "1px solid #ccc",
    maxWidth: "400px",
    margin: "20px auto",
  },
  title: { textAlign: "center" },
  status: { textAlign: "center", fontStyle: "italic" },
  scoreRow: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: "20px",
  },
  team: { textAlign: "center" },
  logo: { width: "60px", height: "60px", objectFit: "contain" },
  teamName: { fontWeight: "bold" },
  score: { fontSize: "1.2em" },
};

export default NBAScoreBoard;
